import React, { useState, useEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Skeleton
} from '@mui/material';
import { useStoreSettings } from '../../contexts/StoreSettingsContext';
// سنستخدم مكون بسيط بدلاً من Swiper لتجنب مشاكل التوافق

// واجهة البانر
export interface Banner {
  id: string;
  title: string;
  subtitle?: string;
  imageUrl: string;
  buttonText?: string;
  buttonLink?: string;
  backgroundColor?: string;
  textColor?: string;
  titleFontSize?: string;  // حجم خط العنوان
  subtitleFontSize?: string;  // حجم خط العنوان الفرعي
  size?: 'small' | 'medium' | 'large';  // حجم البانر: صغير، متوسط، كبير
}

interface StoreBannerProps {
  banners?: Banner[];
  loading?: boolean;
}

const defaultBanners: Banner[] = [
  {
    id: '1',
    title: 'عروض حصرية على الأجهزة الإلكترونية',
    subtitle: 'خصومات تصل إلى 30% على جميع المنتجات',
    imageUrl: 'https://source.unsplash.com/random/1200x400/?electronics',
    buttonText: 'تسوق الآن',
    buttonLink: '/store/categories',
    backgroundColor: '#0d51d6',
    textColor: '#ffffff',
    titleFontSize: '2.5rem',
    subtitleFontSize: '1.25rem'
  },
  {
    id: '2',
    title: 'أحدث الهواتف الذكية',
    subtitle: 'اكتشف أحدث التقنيات بأفضل الأسعار',
    imageUrl: 'https://source.unsplash.com/random/1200x400/?smartphone',
    buttonText: 'اكتشف المزيد',
    buttonLink: '/product/smartphone',
    backgroundColor: '#673ab7',
    textColor: '#ffffff',
    titleFontSize: '2.5rem',
    subtitleFontSize: '1.25rem'
  },
  {
    id: '3',
    title: 'خدمات الصيانة المتميزة',
    subtitle: 'صيانة احترافية لجميع أنواع الأجهزة',
    imageUrl: 'https://source.unsplash.com/random/1200x400/?repair',
    buttonText: 'طلب صيانة',
    buttonLink: '/maintenance/create',
    backgroundColor: '#2196f3',
    textColor: '#ffffff',
    titleFontSize: '2.5rem',
    subtitleFontSize: '1.25rem'
  }
];

const StoreBanner: React.FC<StoreBannerProps> = ({ banners = defaultBanners, loading = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { settings } = useStoreSettings();

  const [displayBanners, setDisplayBanners] = useState<Banner[]>(banners);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [isVisible, setIsVisible] = useState<boolean | null>(null);
  const initialRender = useRef(true);
  const hasBannersLoaded = useRef(false);
  
  // تحديد أحجام الخط الافتراضية بناءً على حجم الشاشة
  const defaultTitleFontSize = isMobile ? '1.5rem' : isTablet ? '2rem' : '2.5rem';
  const defaultSubtitleFontSize = isMobile ? '0.875rem' : isTablet ? '1rem' : '1.25rem';
  
  // عند تحميل المكون للمرة الأولى، نستخدم قيمة null لتجنب وميض الإعلانات
  useEffect(() => {
    // تحقق من إعدادات ظهور البانر فقط عند التحميل الأول
    if (initialRender.current && settings?.display) {
      setIsVisible(settings.display.showBanners !== false);
      initialRender.current = false;
    }
  }, [settings]);

  // تحديث البانرات عند تغيير props مع تجنب إعادة تعيين البانرات الفارغة
  useEffect(() => {
    if (banners && banners.length > 0) {
      // التأكد من أن لكل بانر قيم افتراضية لأحجام الخطوط إذا لم تكن محددة
      const updatedBanners = banners.map(banner => ({
        ...banner,
        titleFontSize: banner.titleFontSize || defaultTitleFontSize,
        subtitleFontSize: banner.subtitleFontSize || defaultSubtitleFontSize
      }));
      
      console.log('تحديث البانرات مع أحجام الخطوط:', updatedBanners);
      setDisplayBanners(updatedBanners);
      hasBannersLoaded.current = true;
    }
  }, [banners, defaultTitleFontSize, defaultSubtitleFontSize]);

  // تغيير البانر كل 5 ثواني
  useEffect(() => {
    if (!displayBanners.length) return;
    
    const interval = setInterval(() => {
      setCurrentBannerIndex((prevIndex) =>
        prevIndex === displayBanners.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [displayBanners.length]);

  // إذا كانت قيمة الرؤية false (الإخفاء المتعمد من الإعدادات)، لا نعرض شيئًا
  if (isVisible === false) {
    return null;
  }

  // إذا كان قيد التحميل، نعرض skeleton
  if (loading) {
    return (
      <Box sx={{ width: '100%', mb: 4 }}>
        <Skeleton variant="rectangular" width="100%" height={isMobile ? 200 : 400} animation="wave" />
      </Box>
    );
  }

  // إذا لم يكن هناك بانرات للعرض
  if (!displayBanners.length) {
    return null;
  }

  const currentBanner = displayBanners[currentBannerIndex];

  return (
    <Box sx={{ width: '100%', mb: 4, overflow: 'hidden', position: 'relative' }}>
      <Paper
        sx={{
          position: 'relative',
          backgroundImage: `url(${currentBanner.imageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: (() => {
            // تحديد ارتفاع البانر حسب الحجم المحدد والجهاز
            const size = currentBanner.size || 'medium';
            if (isMobile) {
              return size === 'small' ? '150px' : size === 'medium' ? '200px' : '250px';
            } else if (isTablet) {
              return size === 'small' ? '200px' : size === 'medium' ? '300px' : '400px';
            } else {
              return size === 'small' ? '250px' : size === 'medium' ? '400px' : '500px';
            }
          })(),
          width: '100%',
          borderRadius: 0,
          transition: 'background-image 0.5s ease-in-out',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        {/* طبقة داكنة شفافة لتحسين قراءة النص */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: 'rgba(0,0,0,0.3)',
            zIndex: 0
          }}
        />
        
        {/* محتوى البانر */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            right: { xs: '5%', md: '10%' },
            transform: 'translateY(-50%)',
            textAlign: 'right',
            color: currentBanner.textColor || 'white',
            zIndex: 1,
            maxWidth: { xs: '85%', md: '50%' },
            textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end'
          }}
        >
          <Typography
            variant={isMobile ? 'h5' : 'h3'}
            component="h2"
            gutterBottom
            sx={{ 
              fontWeight: 'bold',
              fontSize: currentBanner.titleFontSize || defaultTitleFontSize,
              wordBreak: 'break-word',
              width: '100%',
              lineHeight: 1.2
            }}
          >
            {currentBanner.title}
          </Typography>

          {currentBanner.subtitle && (
            <Typography
              variant={isMobile ? 'body2' : 'h6'}
              sx={{ 
                mb: 3, 
                display: { xs: 'none', sm: 'block' }, 
                textShadow: '1px 1px 3px rgba(0,0,0,0.7)',
                fontSize: currentBanner.subtitleFontSize || defaultSubtitleFontSize,
                wordBreak: 'break-word',
                width: '100%',
                lineHeight: 1.4
              }}
            >
              {currentBanner.subtitle}
            </Typography>
          )}

          {currentBanner.buttonText && currentBanner.buttonLink && (
            <Button
              variant="contained"
              color="primary"
              component={RouterLink}
              to={currentBanner.buttonLink}
              size={isMobile ? 'small' : 'medium'}
              sx={{
                mt: 2,
                fontWeight: 'bold',
                backgroundColor: currentBanner.backgroundColor || theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: 'white',
                  color: currentBanner.backgroundColor || theme.palette.primary.main,
                },
                maxWidth: '100%',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {currentBanner.buttonText}
            </Button>
          )}
        </Box>
      </Paper>

      {/* مؤشرات البانر */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 1
        }}
      >
        {displayBanners.map((_, index) => (
          <Box
            key={index}
            onClick={() => setCurrentBannerIndex(index)}
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: index === currentBannerIndex ? 'primary.main' : 'rgba(255,255,255,0.5)',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default StoreBanner;
